import type {
	FormSchemaDataField,
	FormSchemaField,
	FormSchemaPresentational,
	FormSchemaSection,
	FormSchemaSections,
	FormSchemaSectionsItem,
	FormSchemaStructuralEntry,
} from '#layers/block-engine/types/form-schema';
import { callOrReturn, isRecord } from '#layers/base/public';
import { FormSchemaKind } from '#layers/block-engine/types/form-schema';

export type FormSchemaEntryMapper<TResult extends object> = (
	entry: FormSchemaField<TResult>,
) => FormSchemaField<TResult> | undefined;

/**
 * Check whether an entry is a data-bound FormSchema field.
 */
export function isFormSchemaDataField<TResult extends object = Record<string, unknown>>(
	entry: unknown,
): entry is FormSchemaDataField<TResult, string> {
	return isRecord(entry) && typeof entry.name === 'string' && entry.name.length > 0;
}

/**
 * Check whether an entry is presentational structural chrome.
 */
export function isFormSchemaPresentationalEntry(
	entry: unknown,
): entry is FormSchemaPresentational {
	return isRecord(entry) && entry.kind === FormSchemaKind.Presentational;
}

/**
 * Check whether an entry is single-section structural chrome.
 */
export function isFormSchemaSectionEntry<TResult extends object>(
	entry: unknown,
): entry is FormSchemaSection<TResult> {
	return getFormSchemaSectionEntry<TResult>(entry) !== undefined;
}

/**
 * Check whether an entry is multi-section structural chrome.
 */
export function isFormSchemaSectionsEntry<TResult extends object>(
	entry: unknown,
): entry is FormSchemaSections<TResult> {
	return getFormSchemaSectionsEntry<TResult>(entry) !== undefined;
}

/**
 * Check whether an entry is one of the closed structural FormSchema kinds.
 */
export function isFormSchemaStructuralEntry<TResult extends object>(
	entry: unknown,
): entry is FormSchemaStructuralEntry<TResult> {
	return isFormSchemaPresentationalEntry(entry)
		|| isFormSchemaSectionEntry<TResult>(entry)
		|| isFormSchemaSectionsEntry<TResult>(entry);
}

/**
 * Check whether an entry should be considered by render-time visibility rules.
 */
export function isFormSchemaEntryVisible(entry: { hidden?: boolean | (() => boolean) }): boolean {
	return !callOrReturn(entry.hidden);
}

/**
 * Check whether a field list contains at least one visible renderable entry.
 */
export function hasVisibleFormSchemaFields<TResult extends object>(
	fields: readonly FormSchemaField<TResult>[],
): boolean {
	return fields.some((field) => {
		if (!isFormSchemaEntryVisible(field)) {
			return false;
		}

		if (isFormSchemaDataField<TResult>(field)) {
			return true;
		}

		if (isFormSchemaPresentationalEntry(field)) {
			return true;
		}

		const sectionEntry = getFormSchemaSectionEntry<TResult>(field);

		if (sectionEntry) {
			return hasVisibleFormSchemaFields(sectionEntry.fields);
		}

		const sectionsEntry = getFormSchemaSectionsEntry<TResult>(field);

		if (sectionsEntry) {
			return getVisibleFormSchemaSections(sectionsEntry).length > 0;
		}

		return false;
	});
}

/**
 * Return sections that should be rendered by a sections chrome block.
 */
export function getVisibleFormSchemaSections<TResult extends object>(
	entry: FormSchemaSections<TResult>,
): FormSchemaSectionsItem<TResult>[] {
	return entry.sections.filter((section) =>
		isFormSchemaEntryVisible(section) && hasVisibleFormSchemaFields(section.fields),
	);
}

/**
 * Visit named fields recursively without applying render-time visibility.
 */
export function visitFormSchemaDataFields<TResult extends object>(
	fields: readonly FormSchemaField<TResult>[],
	visitor: (field: FormSchemaDataField<TResult, string>) => void,
): void {
	for (const field of fields) {
		if (isFormSchemaDataField<TResult>(field)) {
			visitor(field);
			continue;
		}

		const sectionEntry = getFormSchemaSectionEntry<TResult>(field);

		if (sectionEntry) {
			visitFormSchemaDataFields(sectionEntry.fields, visitor);
		}
		else {
			const sectionsEntry = getFormSchemaSectionsEntry<TResult>(field);

			if (!sectionsEntry) {
				continue;
			}

			for (const section of sectionsEntry.sections) {
				visitFormSchemaDataFields(section.fields, visitor);
			}
		}
	}
}

/** Returns data-field names in their schema traversal order. */
export function getFormSchemaFieldNames<TResult extends object>(fields: readonly FormSchemaField<TResult>[]): string[] {
	const names: string[] = [];

	visitFormSchemaDataFields(fields, (field) => {
		names.push(field.name);
	});

	return names;
}

/**
 * Recursively map entries and remove entries when the mapper returns undefined.
 */
export function mapFormSchemaEntries<TResult extends object>(
	entries: readonly FormSchemaField<TResult>[],
	mapper: FormSchemaEntryMapper<TResult>,
): FormSchemaField<TResult>[] {
	const result: FormSchemaField<TResult>[] = [];

	for (const entry of entries) {
		const mappedEntry = mapFormSchemaEntry(entry, mapper);

		if (mappedEntry) {
			result.push(mappedEntry);
		}
	}

	return result;
}

/**
 * Map an entry after recursively mapping its nested structural children.
 */
function mapFormSchemaEntry<TResult extends object>(
	entry: FormSchemaField<TResult>,
	mapper: FormSchemaEntryMapper<TResult>,
): FormSchemaField<TResult> | undefined {
	const sectionEntry = getFormSchemaSectionEntry<TResult>(entry);

	if (sectionEntry) {
		return mapper({
			...sectionEntry,
			fields: mapFormSchemaEntries(sectionEntry.fields, mapper),
		} as FormSchemaField<TResult>);
	}

	const sectionsEntry = getFormSchemaSectionsEntry<TResult>(entry);

	if (sectionsEntry) {
		return mapper({
			...sectionsEntry,
			sections: sectionsEntry.sections.map((section) => ({
				...section,
				fields: mapFormSchemaEntries(section.fields, mapper),
			})),
		} as FormSchemaField<TResult>);
	}

	return mapper(entry);
}

/** Narrow an unknown value to one section entry. */
function getFormSchemaSectionEntry<TResult extends object>(
	entry: unknown,
): FormSchemaSection<TResult> | undefined {
	return getStructuralEntryOfKind<TResult, typeof FormSchemaKind.Section>(entry, FormSchemaKind.Section);
}

/** Narrow an unknown value to one sections entry. */
function getFormSchemaSectionsEntry<TResult extends object>(
	entry: unknown,
): FormSchemaSections<TResult> | undefined {
	return getStructuralEntryOfKind<TResult, typeof FormSchemaKind.Sections>(entry, FormSchemaKind.Sections);
}

/**
 * Narrow an unknown value to one structural FormSchema entry kind.
 */
function getStructuralEntryOfKind<
	TResult extends object,
	TKind extends typeof FormSchemaKind.Section | typeof FormSchemaKind.Sections,
>(entry: unknown, kind: TKind): Extract<FormSchemaStructuralEntry<TResult>, { kind: TKind }> | undefined {
	if (!isRecord(entry) || entry.kind !== kind) {
		return;
	}

	const structuralEntry: unknown = entry;
	return structuralEntry as Extract<FormSchemaStructuralEntry<TResult>, { kind: TKind }>;
}
